import {
  ChainOfThoughtStepStatus,
  MessageResponseTypes,
  type ChainOfThoughtStep,
  type GenericItem,
  type StreamChunk,
} from '@carbon/ai-chat';

import type { ErrorCopy, SseEvent, ToolStatus } from '../types';

// Our tool statuses onto Carbon's chain-of-thought step statuses. Carbon has
// no "stopped"/"unknown"; both read as a step that did not succeed.
const STEP_STATUS: Record<ToolStatus, ChainOfThoughtStepStatus> = {
  pending: ChainOfThoughtStepStatus.PROCESSING,
  success: ChainOfThoughtStepStatus.SUCCESS,
  error: ChainOfThoughtStepStatus.FAILURE,
  stopped: ChainOfThoughtStepStatus.FAILURE,
  unknown: ChainOfThoughtStepStatus.FAILURE,
};

export type StreamTranslatorOptions = {
  /** Id shared by every chunk of this response — Carbon groups the stream by it. */
  responseId: string;
  /** Receives each chunk in order. */
  emit: (chunk: StreamChunk) => Promise<void> | void;
  /** Page-specific copy for server `error` reasons. */
  errorCopy?: ErrorCopy;
  /** Fired once, when the server names the conversation this run belongs to. */
  onConversationId?: (conversationId: number) => void;
};

// Translates our SSE event stream (meta / assistant_text / tool_call_start /
// tool_call_complete / final / error / done) into Carbon AI Chat stream chunks.
//
// Ordering matters: Carbon merges partial chunks by item id, and the final
// response replaces the streamed items wholesale, so this keeps an ordered
// `items` list and emits it as the final response. Chunk emission is serialized
// through a promise chain because `addMessageChunk` is async while the SSE
// reader hands us events synchronously — without the chain, a slow chunk could
// land after a later one and reorder the transcript.
export function createStreamTranslator({
  responseId,
  emit,
  errorCopy = {},
  onConversationId,
}: StreamTranslatorOptions) {
  /** Final item list, in arrival order, used for the final response. */
  const items: GenericItem[] = [];
  // Chain-of-thought steps in call order, plus an index so a completion can
  // patch its step in place.
  const steps: ChainOfThoughtStep[] = [];
  const stepIndexById = new Map<string, number>();
  /** Id of the text item currently accumulating deltas, if any. */
  let textItemId: string | null = null;
  let textSoFar = '';

  let queue: Promise<void> = Promise.resolve();
  const enqueue = (chunk: StreamChunk) => {
    queue = queue.then(() => emit(chunk)).catch(() => undefined);
  };

  // `cancellable` marks the run as stoppable, which is what renders Carbon's
  // stop button. Only the streamed chunks need it — the runtime reads it from
  // partial items alone and resets the button on complete_item/final_response.
  const textItem = (
    id: string,
    text: string,
    { streaming = false }: { streaming?: boolean } = {}
  ): GenericItem =>
    ({
      response_type: MessageResponseTypes.TEXT,
      text,
      streaming_metadata: streaming ? { id, cancellable: true } : { id },
    }) as GenericItem;

  // Carbon merges message_options index-wise, so re-sending the whole array is
  // correct. It must ride on a chunk that carries a partial_item, though: the
  // chunk dispatcher ignores anything without one, so a partial_response-only
  // chunk is silently dropped and the steps would not appear until the final
  // response. Riding an empty text delta keeps the transcript unchanged while
  // carrying the updated steps.
  const emitSteps = () => {
    if (textItemId === null) {
      textItemId = `${responseId}-text-${items.length}`;
      textSoFar = '';
      items.push(textItem(textItemId, ''));
    }
    enqueue({
      partial_item: textItem(textItemId, '', { streaming: true }),
      partial_response: { message_options: { chain_of_thought: steps } },
      streaming_metadata: { response_id: responseId },
    } as StreamChunk);
  };

  /** Start a fresh text item; the next `assistant_text` opens a new bubble. */
  const closeTextItem = () => {
    textItemId = null;
    textSoFar = '';
  };

  const appendText = (text: string) => {
    if (!text) return;
    if (textItemId === null) {
      textItemId = `${responseId}-text-${items.length}`;
      textSoFar = '';
      items.push(textItem(textItemId, ''));
    }
    textSoFar += text;
    const index = items.findIndex(
      (item) => item.streaming_metadata?.id === textItemId
    );
    if (index !== -1) items[index] = textItem(textItemId, textSoFar);

    // Carbon appends the delta for repeated partial chunks with the same id.
    enqueue({
      partial_item: textItem(textItemId, text, { streaming: true }),
      streaming_metadata: { response_id: responseId },
    } as StreamChunk);
  };

  return {
    handle(event: SseEvent) {
      switch (event.event) {
        case 'meta': {
          const id = event.data.conversation_id;
          if (typeof id === 'number') onConversationId?.(id);
          return;
        }

        case 'assistant_text': {
          appendText(event.data.text);
          return;
        }

        case 'tool_call_start': {
          stepIndexById.set(event.data.id, steps.length);
          steps.push({
            title: event.data.name,
            tool_name: event.data.name,
            request: { args: event.data.input },
            status: ChainOfThoughtStepStatus.PROCESSING,
          });
          emitSteps();
          return;
        }

        case 'tool_call_complete': {
          const index = stepIndexById.get(event.data.id);
          const previous = index === undefined ? undefined : steps[index];
          const step: ChainOfThoughtStep = {
            title: event.data.name ?? previous?.title ?? 'tool',
            tool_name: event.data.name ?? previous?.tool_name,
            request: previous?.request ?? { args: {} },
            response: { content: event.data.output },
            status: STEP_STATUS[event.data.status] ?? STEP_STATUS.unknown,
            description:
              event.data.error ??
              (event.data.duration_ms === null
                ? undefined
                : `Completed in ${event.data.duration_ms} ms`),
          };
          if (index === undefined) {
            stepIndexById.set(event.data.id, steps.length);
            steps.push(step);
          } else {
            steps[index] = step;
          }
          emitSteps();
          return;
        }

        case 'final': {
          // The server sends the full text only when it never streamed deltas.
          if (!items.some((item) => item.response_type === 'text')) {
            const finalText = event.data.final_response;
            if (finalText) appendText(finalText);
          }
          return;
        }

        case 'error': {
          const { reason, detail, error } = event.data;
          const mapped = reason ? errorCopy[reason] : undefined;
          closeTextItem();
          // Carbon styles inline_error as an error state rather than prose.
          const item = {
            response_type: MessageResponseTypes.INLINE_ERROR,
            text: mapped ?? error,
            // Without an id the reducer files every id-less item under the same
            // null key, so a second error would append to the first.
            streaming_metadata: { id: `${responseId}-error-${items.length}` },
            ...(detail ? { debug: { text: detail } } : {}),
          } as unknown as GenericItem;
          items.push(item);
          enqueue({
            partial_item: item,
            streaming_metadata: { response_id: responseId },
          } as StreamChunk);
          return;
        }

        case 'done':
        default:
      }
    },

    // Emit the final response and resolve once every chunk has been applied.
    async finish(): Promise<void> {
      // Every streamed item needs a complete_item chunk carrying its full
      // text: that is what sets streamingState.isDone and switches the renderer
      // from the joined chunks to the item's own text. Without it a trailing
      // markdown table sits in its loading skeleton. Re-sending for an
      // already-complete item is idempotent — the runtime merges in place.
      for (const item of items) {
        if (!item.streaming_metadata?.id) continue;
        enqueue({
          complete_item: item,
          streaming_metadata: { response_id: responseId },
        } as StreamChunk);
      }

      enqueue({
        final_response: {
          id: responseId,
          output: { generic: items },
          ...(steps.length
            ? { message_options: { chain_of_thought: steps } }
            : {}),
        },
      } as unknown as StreamChunk);
      await queue;
    },

    // Flush whatever streamed before a transport failure. The caller rethrows
    // so Carbon shows its own failed-send treatment on the user's message.
    async abandon({
      stopped = false,
    }: { stopped?: boolean } = {}): Promise<void> {
      // Nothing streamed means there is nothing to finalize; emitting a final
      // response here would create an empty, invisible message.
      if (!items.length) {
        await queue;
        return;
      }
      if (stopped) {
        // Renders Carbon's "response stopped" note rather than leaving the
        // truncated text looking like a complete answer.
        for (const item of items) {
          if (!item.streaming_metadata?.id) continue;
          item.streaming_metadata = {
            ...item.streaming_metadata,
            stream_stopped: true,
          };
        }
      }
      await this.finish();
    },
  };
}
